import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { LclContainerService } from '@shared/lcl-mutual/services';
import { PackageData } from '@app/modules/lcl-shipping/types';
import { PackageUpdateModel } from '@data-layer/models/lcl';
import { enumLengthUnit, enumWeightUnit } from '@data-layer/enums/common';
import { LclShippingPackageEditComponent } from '@shared/lcl-mutual/components';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';

@Component({
  selector: 'app-lcl-create-quote-packages',
  template: `
    <div class="lcl-packages-wrapper" *ngIf="isInCard">
      <app-lcl-card cardTitle="LCL_SHIPMENT.QUOTE.CREATE.WHAT_ARE_YOU_SHIPPING">
        <ng-container body>
          <div class="position-relative">
            <ng-container [ngTemplateOutlet]="packages"></ng-container>

            <loading-spinner [show]="isLoading"></loading-spinner>
          </div>
        </ng-container>
      </app-lcl-card>
    </div>

    <ng-container *ngIf="!isInCard" [ngTemplateOutlet]="packages"></ng-container>

    <ng-template #packages>
      <div *ngFor="let package of packagesFormArray.controls; let i = index; let last = last" class="mb-2 mb-xl-4">
        <div
          class="lcl-packages-item-form"
          [ngClass]="{
            'lcl-packages-item-form-external': isExternal,
            'lcl-packages-item-form-external-last': isExternal && last,
            'lcl-packages-item-form-quote': isQuoteEdit,
            'lcl-packages-item-form-quote-create': isQuoteCreate
          }"
          *ngIf="isItemEditable(i)"
        >
          <app-lcl-shipping-package-edit
            #packagesViewItem
            [formGroup]="package"
            [index]="i"
            [isLastest]="packagesFormArray.controls.length === 1"
            [isSidebar]="false"
            [isExternal]="isExternal"
            (removePackage)="onPackageRemoved(i)"
            (formUpdated)="onFormUpdated($event)"
          ></app-lcl-shipping-package-edit>

          <div class="w-100 text-center d-xl-none">
            <button
              type="button"
              class="btn btn-primary btn-confirm-package-mobile mt-3"
              *ngIf="isFormEdited && (isExternal || isQuoteEdit)"
              (click)="confirmPackage.emit()"
            >
              {{ 'BUTTON.CONFIRM' | translate }}
            </button>
          </div>
        </div>

        <div class="d-xl-block">
          <app-lcl-shipping-package-item-view
            *ngIf="!isItemEditable(i)"
            [package]="package.value"
            [isLastest]="packagesFormArray.controls.length === 1"
            (removePackage)="onPackageRemoved(i)"
            (editPackage)="onEditPackage(i)"
          ></app-lcl-shipping-package-item-view>
        </div>
      </div>

      <div class="lcl-packages-footer">
        <div class="lcl-packages-line-and-btn">
          <div class="lcl-packages-item-line d-none d-xl-block"></div>
          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-add-package d-none d-xl-inline" (click)="addPackage.emit()">
              <div class="icon-plus-container">
                <icon-plus></icon-plus>
              </div>
              {{ 'ADD_ANOTHER_LOAD' | translate }}
            </button>

            <div class="w-100 d-xl-none text-center">
              <button
                type="button"
                class="btn btn-add-package-mobile"
                *ngIf="!isFormEdited && !isExternal && !isQuoteCreate"
                (click)="addPackage.emit()"
              >
                <div class="icon-plus-container">
                  <icon-plus></icon-plus>
                </div>
                {{ 'ADD_LOAD_DETAILS' | translate }}
              </button>

              <button
                type="button"
                class="btn btn-primary btn-confirm-package-mobile mt-3"
                *ngIf="isFormEdited && !isExternal && !isQuoteEdit"
                (click)="confirmPackage.emit()"
              >
                {{ 'BUTTON.CONFIRM' | translate }}
              </button>
            </div>
          </div>
        </div>

        <div
          class="lcl-packages-totals d-xl-flex"
          [ngClass]="{ 'd-none': isFormEdited, 'd-none d-xl-flex': isExternal || isQuoteCreate, 'is-booking': isBookingEdit }"
        >
          <p class="lcl-packages-totals-header">{{ 'LABELS.TOTALS' | translate }}:</p>

          <span class="d-none d-xl-inline">{{ 'UNITS' | translate }}</span>
          <p class="lcl-packages-totals-sub-header">{{ totalUnits }}</p>

          <span>{{ 'LABELS.WEIGHT' | translate }}</span>
          <p class="lcl-packages-totals-sub-header">{{ totalWeight }} {{ 'WEIGHT_UNIT.KG' | translate }}</p>

          <span>{{ 'LABELS.VOLUME' | translate }}</span>
          <p class="lcl-packages-totals-sub-header">{{ totalValue }} {{ 'LENGTH_UNIT.CBM' | translate }}</p>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./lcl-create-quote-packages.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclCreateQuotePackagesComponent implements OnInit, OnDestroy {
  @Input() public packagesFormArray: UntypedFormArray;
  @Input() public isInCard: boolean = true;
  @Input() public isExternal: boolean;
  @Input() public isMobile: boolean;
  @Input() public isSidebar: boolean;
  @Input() public isLoading: boolean;
  @Input() public isQuoteCreate: boolean;
  @Input() public isQuoteEdit: boolean;
  @Input() public isBookingEdit: boolean;
  @Output() public addPackage: EventEmitter<void> = new EventEmitter<void>();
  @Output() public confirmPackage: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editPackage: EventEmitter<number> = new EventEmitter<number>();
  @Output() public removePackage: EventEmitter<number> = new EventEmitter<number>();

  public totalValue: number = 0;
  public totalWeight: number = 0;
  public totalUnits: number = 0;

  public isFormEdited: boolean;

  @ViewChild('packagesViewItem', { static: false })
  public packagesViewItem: LclShippingPackageEditComponent;

  public weightSwitchControl: UntypedFormControl = new UntypedFormControl(false);

  private oldUnitValues: { lengthUnit: enumLengthUnit; weightUnit: enumWeightUnit } = {
    lengthUnit: enumLengthUnit.CENTIMETER,
    weightUnit: enumWeightUnit.KG,
  };

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private lclContainerService: LclContainerService, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.isFormEdited = this.checkIsFormEdited;

    this.weightSwitchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.packagesFormArray.controls.forEach((item: AbstractControl) =>
        item.patchValue(
          val
            ? { weightUnit: enumWeightUnit.LB, lengthUnit: enumLengthUnit.INCH }
            : { weightUnit: enumWeightUnit.KG, lengthUnit: enumLengthUnit.CENTIMETER },
        ),
      );
    });

    this.packagesFormArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isFormEdited = this.checkIsFormEdited;

      this.calcTotals();
    });

    this.calcTotals();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onFormUpdated(data: PackageData): void {
    for (const item of this.packagesFormArray.controls) {
      // imperial <-> metric dropdown update
      if (this.oldUnitValues.lengthUnit !== data.lengthUnit) {
        data.weightUnit = data.lengthUnit === enumLengthUnit.CENTIMETER ? enumWeightUnit.KG : enumWeightUnit.LB;
      }
      if (this.oldUnitValues.weightUnit !== data.weightUnit) {
        data.lengthUnit = data.weightUnit === enumWeightUnit.KG ? enumLengthUnit.CENTIMETER : enumLengthUnit.INCH;
      }
      item.patchValue({ lengthUnit: data.lengthUnit, weightUnit: data.weightUnit }, { emitEvent: false });
    }

    this.oldUnitValues.lengthUnit = data.lengthUnit;
    this.oldUnitValues.weightUnit = data.weightUnit;

    this.calcTotals();
  }

  public onPackageRemoved(id: number): void {
    this.removePackage.emit(id);

    this.calcTotals();
  }

  public onEditPackage(index: number): void {
    if (!this.isSidebar || this.isMobile) {
      this.packagesFormArray.controls[index].get('isView').setValue(false);
    } else {
      this.editPackage.emit(index);
    }
  }

  public forceUpdateForm(): void {
    if (!!this.packagesViewItem) {
      this.packagesViewItem.forceUpdateForm();
      this.cdr.markForCheck();
    }
  }

  public isItemEditable(index: number): boolean {
    return !this.packagesFormArray.controls[index].get('isView').value;
  }

  public get checkIsFormEdited(): boolean {
    return this.packagesFormArray.controls.some((item: AbstractControl) => !Boolean(item.get('isView').value));
  }

  private calcTotals(): void {
    const values: PackageUpdateModel[] = this.packagesFormArray.getRawValue();

    this.totalValue = this.lclContainerService.totalCBM(values);

    this.totalWeight = this.lclContainerService.totalWeight(values);

    this.totalUnits = this.lclContainerService.totalUnits(values);
  }
}
